import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle, faExclamationTriangle,
  faMagnifyingGlass, faEnvelopeOpenText,
} from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import api from "../services/api";
import styles from "./SendEmailModal.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const ROLE_LABELS = { admin: "Admin", professor: "Professor", aluno: "Aluno", google: "Google" };
const FAIL_REASON_LABELS = { bounced: "email não entregável (bounce)", send_failed: "falha no envio" };

export default function SendEmailModal({ open, onClose }) {
  useBodyScrollLock(open);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");      // ""=todos | admin | professor | aluno | google
  const [onlySelfService, setOnlySelfService] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [planFilter, setPlanFilter] = useState("");       // ""=todos | sem | vencido | vencendo | vigente
  const [hideBounced, setHideBounced] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (_) {
      // silencioso
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadUsers();
      setSearch("");
      setRoleFilter("");
      setOnlySelfService(false);
      setOnlyActive(false);
      setPlanFilter("");
      setHideBounced(false);
      setSelectedIds(new Set());
      setSubject("");
      setBody("");
      setSuccess(null);
      setError(null);
      setConfirming(false);
    }
  }, [open, loadUsers]);

  // Status do plano a partir de plan_expires_at (data YYYY-MM-DD).
  const planStatusOf = (u) => {
    if (!u.plan_expires_at) return "sem";
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const exp = new Date(`${u.plan_expires_at}T00:00:00`);
    const dias = Math.floor((exp - today) / 86400000);
    if (dias < 0) return "vencido";
    if (dias <= 7) return "vencendo";
    return "vigente";
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !(u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))) return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (onlySelfService && !u.is_self_service) return false;
      if (onlyActive && !u.is_active) return false;
      if (planFilter && planStatusOf(u) !== planFilter) return false;
      if (hideBounced && u.email_bounced_at) return false;
      return true;
    });
  }, [users, search, roleFilter, onlySelfService, onlyActive, planFilter, hideBounced]);

  const hasActiveFilters = !!search || !!roleFilter || onlySelfService || onlyActive || !!planFilter || hideBounced;
  const clearFilters = () => {
    setSearch("");
    setRoleFilter("");
    setOnlySelfService(false);
    setOnlyActive(false);
    setPlanFilter("");
    setHideBounced(false);
  };

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.has(u.id));

  const toggleUser = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredUsers.forEach((u) => next.delete(u.id));
      } else {
        filteredUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedIds.size === 0 || !subject.trim() || !body.trim()) return;
    setError(null);
    setSuccess(null);
    setConfirming(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post("/users/send-email", {
        user_ids: Array.from(selectedIds),
        subject: subject.trim(),
        body: body.trim(),
      });
      const { sent = 0, total = 0, failed = [] } = res.data || {};
      const failedList = failed
        .map((f) => `${f.email} (${FAIL_REASON_LABELS[f.reason] || f.reason})`)
        .join(", ");

      if (sent === 0 && failed.length > 0) {
        // Nenhum email saiu — não fecha, admin precisa ver o que falhou
        setError(`Não foi possível enviar para nenhum destinatário: ${failedList}.`);
      } else if (failed.length > 0) {
        // Sucesso parcial — mantém o modal aberto pra revisão
        setSuccess(`Enviado para ${sent} de ${total} destinatário(s).`);
        setError(`Falha ao enviar para: ${failedList}.`);
      } else {
        // Sucesso total — mostra confirmação e fecha o modal sozinho
        setSuccess(`Email enviado para ${sent} destinatário(s).`);
        setSelectedIds(new Set());
        setSubject("");
        setBody("");
        window.setTimeout(() => { onClose(); }, 1800);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "object" ? detail.message : detail || "Erro ao enviar email.");
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <ModalHeader onClose={onClose} disabled={sending} />

        <div className={styles.header}>
          <p className={styles.title}>Enviar email</p>
          <p className={styles.sub}>Envie uma mensagem por email para um ou mais usuários do sistema</p>
        </div>

        <div className={styles.body}>
          {/* Seleção de destinatários */}
          <div className={styles.recipientsSection}>
            <div className={styles.recipientsHeader}>
              <span className={styles.recipientsLabel}>Destinatários</span>
              {selectedIds.size > 0 && (
                <span className={styles.selectedCount}>{selectedIds.size} selecionado(s)</span>
              )}
            </div>

            <div className={styles.searchRow}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Buscar por nome ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className={styles.selectAllBtn}
                onClick={toggleSelectAllFiltered}
                disabled={filteredUsers.length === 0}
              >
                {allFilteredSelected ? "Limpar seleção" : "Selecionar todos"}
              </button>
            </div>

            <div className={styles.filterRow}>
              <select
                className={styles.filterSelect}
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                aria-label="Filtrar por tipo de usuário"
              >
                <option value="">Todos os tipos</option>
                <option value="admin">Admin</option>
                <option value="professor">Professor</option>
                <option value="aluno">Aluno</option>
                <option value="google">Google</option>
              </select>

              <label className={styles.filterToggle}>
                <input
                  type="checkbox"
                  checked={onlySelfService}
                  onChange={(e) => setOnlySelfService(e.target.checked)}
                />
                <span>Self-service</span>
              </label>

              <label className={styles.filterToggle}>
                <input
                  type="checkbox"
                  checked={onlyActive}
                  onChange={(e) => setOnlyActive(e.target.checked)}
                />
                <span>Somente ativos</span>
              </label>

              <select
                className={styles.filterSelect}
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                aria-label="Filtrar por status do plano"
              >
                <option value="">Todos os planos</option>
                <option value="vigente">Plano vigente</option>
                <option value="vencendo">Vencendo (≤7 dias)</option>
                <option value="vencido">Plano vencido</option>
                <option value="sem">Sem plano</option>
              </select>

              <label className={styles.filterToggle}>
                <input
                  type="checkbox"
                  checked={hideBounced}
                  onChange={(e) => setHideBounced(e.target.checked)}
                />
                <span>Ocultar bounce</span>
              </label>
            </div>

            <div className={styles.filterMeta}>
              <span className={styles.filterCount}>
                {filteredUsers.length} de {users.length} usuário(s)
              </span>
              {hasActiveFilters && (
                <button type="button" className={styles.clearFiltersBtn} onClick={clearFilters}>
                  Limpar filtros
                </button>
              )}
            </div>

            <div className={styles.userList}>
              {loadingUsers && <div className={styles.emptyState}>Carregando usuários...</div>}

              {!loadingUsers && filteredUsers.length === 0 && (
                <div className={styles.emptyState}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} /> Nenhum usuário encontrado.
                </div>
              )}

              {!loadingUsers &&
                filteredUsers.map((u) => {
                  const checked = selectedIds.has(u.id);
                  return (
                    <label
                      key={u.id}
                      className={`${styles.userRow} ${checked ? styles.userRowSelected : ""}`}
                    >
                      <input
                        type="checkbox"
                        className={styles.userCheckbox}
                        checked={checked}
                        onChange={() => toggleUser(u.id)}
                      />
                      <div className={styles.userInfo}>
                        <span className={styles.userName}>{u.name}</span>
                        <span className={styles.userEmail}>{u.email}</span>
                      </div>
                      <span className={styles.roleBadge}>{ROLE_LABELS[u.role] || u.role}</span>
                    </label>
                  );
                })}
            </div>
          </div>

          {/* Confirmação de envio */}
          {confirming && (
            <div className={styles.confirmBox}>
              <p className={styles.confirmMsg}>
                Enviar este email para <strong>{selectedIds.size}</strong> destinatário(s)?
              </p>
              <div className={styles.confirmActions}>
                <button
                  type="button"
                  className={styles.confirmYes}
                  onClick={handleConfirmSend}
                  disabled={sending}
                >
                  {sending ? "Enviando..." : "Sim, enviar"}
                </button>
                <button
                  type="button"
                  className={styles.confirmNo}
                  onClick={() => setConfirming(false)}
                  disabled={sending}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Conteúdo do email */}
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="admin-email-subject" className={styles.label}>Título</label>
              <input
                id="admin-email-subject"
                className={styles.input}
                type="text"
                placeholder="Ex.: Manutenção programada no app"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={150}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="admin-email-body" className={styles.label}>Descrição</label>
              <textarea
                id="admin-email-body"
                className={styles.textarea}
                placeholder="Escreva a mensagem que será enviada..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={8000}
                required
              />
              <span className={styles.charCount}>{body.length}/8000</span>
            </div>

            {success && (
              <div className={styles.feedbackSuccess}>
                <FontAwesomeIcon icon={faCheckCircle} /> {success}
              </div>
            )}
            {error && (
              <div className={styles.feedbackError}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              </div>
            )}

            <button
              type="submit"
              className={styles.sendBtn}
              disabled={sending || selectedIds.size === 0 || !subject.trim() || !body.trim()}
            >
              <FontAwesomeIcon icon={faEnvelopeOpenText} />
              Enviar email
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
